import type { GameModuleValidationResult, ThriveGameModule } from '../sdk';
import type { GameId } from '../shared/types/ids';
import type { GameplayTunables } from '../shared/types/tunables';
import type { GameRegistryEntry } from './types';
import { validateGameModuleDeep } from './validate-module';

/**
 * Registered games (architecture §8). Modules load only when launched, via a relative dynamic
 * `import()` of the game's entry point — never its internals (D-003). There is no remote
 * marketplace and no runtime installation of unknown code.
 */
export const gameRegistry: readonly GameRegistryEntry[] = [
  {
    id: 'spider-math',
    enabled: true,
    featured: true,
    title: 'Spider Math',
    tagline: 'Move your body to fix Dr. Spotty’s scrambled number portals.',
    load: async () => {
      const loaded = await import('../../games/spider-math');
      return loaded.spiderMathModule;
    },
  },
];

export function listEnabledGames(): GameRegistryEntry[] {
  return gameRegistry.filter((entry) => entry.enabled);
}

export function findGameEntry(id: GameId): GameRegistryEntry | undefined {
  return gameRegistry.find((entry) => entry.id === id);
}

export type GameLoadFailureReason =
  'NOT_REGISTERED' | 'DISABLED' | 'IMPORT_FAILED' | 'ID_MISMATCH' | 'INVALID_MODULE';

export interface GameLoadSuccess {
  ok: true;
  gameId: GameId;
  entry: GameRegistryEntry;
  module: ThriveGameModule;
  validation: GameModuleValidationResult;
}

export interface GameLoadFailure {
  ok: false;
  gameId: GameId;
  reason: GameLoadFailureReason;
  message: string;
  validation?: GameModuleValidationResult;
  cause?: unknown;
}

export type GameLoadResult = GameLoadSuccess | GameLoadFailure;

/**
 * The only supported launch path. Every module validates before launch and an invalid module
 * is refused with a structured reason rather than an exception, so the shell can render a
 * friendly "this game needs a fix" screen instead of a blank page.
 */
export interface LoadAndValidateOptions {
  registry?: readonly GameRegistryEntry[];
  /** Injected into mission building during deep validation (D-010). */
  tunables?: GameplayTunables;
}

export async function loadAndValidate(
  gameId: GameId,
  registryOrOptions: readonly GameRegistryEntry[] | LoadAndValidateOptions = {},
): Promise<GameLoadResult> {
  const options: LoadAndValidateOptions = Array.isArray(registryOrOptions)
    ? { registry: registryOrOptions }
    : (registryOrOptions as LoadAndValidateOptions);
  const registry = options.registry ?? gameRegistry;

  const entry = registry.find((candidate) => candidate.id === gameId);

  if (entry === undefined) {
    return {
      ok: false,
      gameId,
      reason: 'NOT_REGISTERED',
      message: `No game is registered with the id "${gameId}".`,
    };
  }

  if (!entry.enabled) {
    return {
      ok: false,
      gameId,
      reason: 'DISABLED',
      message: `The game "${gameId}" is registered but not enabled.`,
    };
  }

  let loaded: unknown;
  try {
    loaded = await entry.load();
  } catch (cause) {
    return {
      ok: false,
      gameId,
      reason: 'IMPORT_FAILED',
      message: `Loading "${gameId}" failed: ${cause instanceof Error ? cause.message : String(cause)}`,
      cause,
    };
  }

  // Validation itself runs against untrusted imported code, so even though both validators
  // promise not to throw, the launch path refuses to be the place where that promise breaks.
  let validation: GameModuleValidationResult;
  try {
    validation = await validateGameModuleDeep(
      loaded,
      options.tunables === undefined ? {} : { tunables: options.tunables },
    );
  } catch (cause) {
    return {
      ok: false,
      gameId,
      reason: 'INVALID_MODULE',
      message: `The game "${gameId}" could not be validated and was not launched.`,
      validation: {
        valid: false,
        errors: [
          {
            code: 'VALIDATION_THREW',
            message: `Validating "${gameId}" threw: ${cause instanceof Error ? cause.message : String(cause)}`,
          },
        ],
        warnings: [],
      },
      cause,
    };
  }

  if (!validation.valid) {
    return {
      ok: false,
      gameId,
      reason: 'INVALID_MODULE',
      message: `The game "${gameId}" failed contract validation and was not launched.`,
      validation,
    };
  }

  const module = loaded as ThriveGameModule;

  let declaredId: string;
  try {
    declaredId = module.id;
  } catch (cause) {
    return {
      ok: false,
      gameId,
      reason: 'INVALID_MODULE',
      message: `The game "${gameId}" could not be identified and was not launched.`,
      cause,
    };
  }

  if (declaredId !== entry.id) {
    // A registry entry pointing at the wrong module would launch the wrong game.
    return {
      ok: false,
      gameId,
      reason: 'ID_MISMATCH',
      message: `Registry entry "${entry.id}" loaded a module declaring id "${declaredId}".`,
      validation,
    };
  }

  return { ok: true, gameId, entry, module, validation };
}
